import * as XLSX from "xlsx";
import * as XlsxPopulate from "xlsx-populate/browser/xlsx-populate";
import { getCurrentDateTime } from "../../../../functions/welding-functions";

// Common s2ab utility function
const s2ab = (s) => {
  const buf = new ArrayBuffer(s.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < s.length; ++i) {
    view[i] = s.charCodeAt(i);
  }
  return buf;
};

// Generic Excel export function
export async function exportToExcel(fileName, data, options = {}) {
  const {
    sheetName = "Report",
    headers = [],
    columnWidths = [],
    title = "",
    companyName = "Godrej & Boyce Mfg. Co. Ltd",
    customProcessor = null
  } = options;

  let wb = XLSX.utils.book_new();
  let todayDate = getCurrentDateTime("-").substring(0, 10);

  // Process data with custom processor if provided
  let processedData = data;
  if (customProcessor) {
    processedData = customProcessor(data);
  }

  let tableData = headers.length > 0 ? [headers, ...processedData] : processedData;
  let alldata = ['', '', '', '', '', '', ''].concat(tableData);

  let ws = XLSX.utils.json_to_sheet(alldata, { skipHeader: true });
  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  const wopts = {
    bookType: "xlsx",
    bookSST: false,
    type: "binary",
  };

  const wbout = XLSX.write(wb, wopts);
  const blob = new Blob([s2ab(wbout)], {
    type: "application/octet-stream",
  });

  XlsxPopulate.fromDataAsync(blob).then((workbook) => {
    let sheet = workbook.sheets()[0];
    sheet.row(8).style('wrapText', true);
    sheet.gridLinesVisible(false);
    sheet.freezePanes(0, 6);

    // Set column widths
    columnWidths.forEach((width, index) => {
      const colLetter = String.fromCharCode(65 + index); // A, B, C, etc.
      sheet.column(colLetter).width(width);
    });

    // Set headers
    sheet.cell("B3").value(companyName);
    sheet.cell("I3").value("As on: " + todayDate);
    if (title) {
      sheet.cell("F4").value(title).style("underline");
    }

    // Style ranges
    const headerRange = `A8:${String.fromCharCode(64 + headers.length)}8`;
    const dataRange = `A9:${String.fromCharCode(64 + headers.length)}${alldata.length}`;

    sheet.range("E4:Y6").style({
      bold: true,
      underline: true
    });

    sheet.range(headerRange).style({
      bold: true,
      horizontalAlignment: 'center',
      border: "thin",
    });

    sheet.range(dataRange).style({
      border: "thin",
    });

    return workbook.outputAsync().then((workbookBlob) => URL.createObjectURL(workbookBlob)).then((url) => {
      const downloadAnchorNode = document.createElement("a");
      downloadAnchorNode.setAttribute("href", url);
      downloadAnchorNode.setAttribute("download", fileName);
      downloadAnchorNode.click();
      downloadAnchorNode.remove();
    });
  });
}

// Welder-specific export function
export async function exportWelderToExcel(fileName, data, options) {
  const {
    colwidth = [],
    Headings = []
  } = options;

  const welderHeaders = {
    A: "",
    B: "Project",
    C: "Slip Date",
    D: "Plant",
    E: "Slip No",
    F: "Cancelled",
    G: "Joint",
    H: "WPS No",
    I: "Process",
    J: "Type",
    K: "Welder Code",
    L: "Welder Name"
  };

  const processedData = [welderHeaders, ...data];
  const columnWidths = colwidth.map(col => col.width);

  await exportToExcel(fileName, processedData, {
    sheetName: "List of Welders report",
    columnWidths,
    title: Headings.find(h => h.col === "F4")?.value || "List of Welders Report",
    customProcessor: (data) => {
      // Custom styling will be handled in the main function
      return data;
    }
  });
}

// Welder Record Sheet export function
export async function exportWelderRecordToExcel(fileName, colwidth, excelData, Data, options) {
  const { titleWelderRange, tbodyHeadRange, tbodyRange, Headings } = options;

  const welderRecordHeaders = {
    A: "Sr No",
    B: "Joint No",
    C: "WPS No",
    D: "WPS Rev No",
    E: "Process",
    F: "Welder Code"
  };

  let wb = XLSX.utils.book_new();
  let data = [welderRecordHeaders, ...Data];
  let alldata = ['', '', '', '', '', ''].concat(data);

  let ws = XLSX.utils.json_to_sheet(alldata, { skipHeader: true });
  XLSX.utils.book_append_sheet(wb, ws, "List of Welders report");

  const wopts = {
    bookType: "xlsx",
    bookSST: false,
    type: "binary",
  };

  const wbout = XLSX.write(wb, wopts);
  const blob = new Blob([s2ab(wbout)], {
    type: "application/octet-stream",
  });

  XlsxPopulate.fromDataAsync(blob).then((workbook) => {
    let sheet = workbook.sheets()[0];
    sheet.row(5).style('wrapText', true);
    sheet.gridLinesVisible(false);
    sheet.freezePanes(0, 2);

    // Set column widths
    for (const col of colwidth) {
      sheet.column(col.col).width(col.width);
    }

    // Set headings with custom styles
    for (const heading of Headings) {
      sheet.cell(heading.col).value(heading.value);
      if (heading.col === "A1") {
        sheet.cell(heading.col).style({ bold: true });
      }
      if (heading.col === "D1" || heading.col === "D2") {
        sheet.cell(heading.col).style({ italic: true });
      }
    }

    // Style ranges
    sheet.range(titleWelderRange).style({
      bold: true,
    });

    sheet.range(tbodyHeadRange).style({
      bold: true,
      horizontalAlignment: 'center',
      border: "thin",
      fontSize: 16,
    });

    sheet.range(`${tbodyRange}:F${alldata.length}`).style({
      border: "thin",
    });

    return workbook.outputAsync().then((workbookBlob) => URL.createObjectURL(workbookBlob)).then((url) => {
      const downloadAnchorNode = document.createElement("a");
      downloadAnchorNode.setAttribute("href", url);
      downloadAnchorNode.setAttribute("download", fileName);
      downloadAnchorNode.click();
      downloadAnchorNode.remove();
    });
  });
}

// Deviation-specific export function
export async function exportDeviationToExcel(fileName, excelData) {
  const headers = {
    A: "Issue Slip No",
    B: "Project No",
    C: "Deviation No",
    D: "Plant No",
    E: "Joint No",
    F: "Welder/ Operator Code",
    G: "Welder/ Operator Name",
    H: "Observer Code",
    I: "Observer Department",
    J: "Observation Date",
    K: "Observation Time (hh:mm)",
    L: "Supervisor Code",
    M: "Supervisor Department",
    N: "Deviation Category",
    O: "Category Description",
    P: "Severity Level",
    Q: "Deviation Text",
    R: "Corrective Action",
    S: "Action Supervisor Code",
    T: "Action Taken Date",
    U: "Action Taken Time (hh:mm)",
    V: "Root Cause Analysis",
    W: "Preventive Action",
    X: "Status",
    Y: "Deviation Status"
  };

  const processedData = excelData.map(row => {
    const capaStatus = (row.devstatus === "Pending for CAPA Analysis" || row.devstatus === "Pending for Closure") ? "Open" : "Closed";

    return {
      A: row.slipNumber,
      B: row.projectNumber,
      C: row.deviationNumber,
      D: row.plantNumber,
      E: row.jointNumber,
      F: row.welderCode,
      G: row.welderName,
      H: row.observerCode,
      I: row.observerDept,
      J: row.observationDate,
      K: row.observationTime,
      L: row.supervisorCode,
      M: row.supervisorDept,
      N: row.deviationCategory,
      O: row.deviationCategoryDesc,
      P: row.deviationSeverityLevel,
      Q: row.deviationText,
      R: row.correctiveAction,
      S: row.actionTakenBy,
      T: row.actionTakenDate,
      U: row.actionTimeTaken,
      V: row.rootCauseAnalysis,
      W: row.preventiveAction,
      X: capaStatus,
      Y: row.devstatus
    };
  });

  const columnWidths = Array(25).fill(15); // 25 columns, width 15
  columnWidths[24] = 22; // Last column wider

  await exportToExcel(fileName, processedData, {
    sheetName: "Deviation Report",
    headers: [headers],
    columnWidths,
    title: "Welding Deviation Report"
  });
}